(() => {
  if (customElements.get('loovi-package-protection')) return;

  const variantId = 53782887170384;
  const root = window.Shopify?.routes?.root || '/';
  const sections = ['cart-drawer', 'cart-icon-bubble'];
  let cart = null;
  let busy = false;
  let auditTimer;
  let auditRequested = false;
  let allowCheckout = false;
  let displayDirty = false;
  let status = '';
  let retryIntent = null;
  let nativeRevision = 0;
  let drawerWasRendered = false;
  let operationDeadline = 0;

  const protectedItems = state => state.items.filter(item => Number(item.variant_id) === variantId && item.quantity > 0);
  const shipmentItems = state => state.items.filter(item => Number(item.variant_id) !== variantId && item.quantity > 0);
  const nativeBusy = () => Boolean(document.querySelector('#CartDrawer .cart__items--disabled, #CartDrawer .cart-item .loading__spinner:not(.hidden)'));
  const validProtection = state => {
    const items = protectedItems(state);
    return items.length === 0 || (shipmentItems(state).length > 0 && items.length === 1 && items[0].quantity === 1);
  };

  function syncViews() {
    document.querySelectorAll('loovi-package-protection').forEach(element => {
      const input = element.querySelector('[data-loovi-protection-toggle]');
      if (!input) return;
      if (cart) {
        // Keep errors and orphaned charges visible so the customer can retry removal.
        element.hidden = !shipmentItems(cart).length && !protectedItems(cart).length && !status;
        input.checked = protectedItems(cart).length > 0;
      }
      input.disabled = busy || !cart || (!shipmentItems(cart).length && !protectedItems(cart).length);
      element.setAttribute('aria-busy', String(busy));
      element.querySelector('[data-loovi-protection-status]').textContent = status;
      const retry = element.querySelector('[data-loovi-protection-retry]');
      retry.hidden = retryIntent === null;
      retry.disabled = busy;
    });
    document.querySelectorAll('#CartDrawer cart-drawer-items').forEach(items => {
      if (busy && !items.hasAttribute('inert')) {
        items.setAttribute('data-loovi-native-lock', '');
        items.setAttribute('inert', '');
      } else if (!busy && items.hasAttribute('data-loovi-native-lock')) {
        items.removeAttribute('data-loovi-native-lock');
        items.removeAttribute('inert');
      }
    });
    const checkout = document.getElementById('CartDrawer-Checkout');
    if (checkout) checkout.disabled = busy || !cart || !shipmentItems(cart).length || !validProtection(cart) || retryIntent !== null;
  }

  async function request(path, body) {
    const remaining = operationDeadline ? operationDeadline - Date.now() : 8000;
    if (remaining <= 0) throw new Error('Cart operation timed out');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), Math.min(remaining, 8000));
    try {
      const url = new URL(path, window.location.origin + root);
      const response = await fetch(url.toString(), {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        // Product URLs negotiate product JSON when Accept is application/json.
        // Section rendering must retain the HTML route even though its envelope is JSON.
        headers: { ...(!url.searchParams.has('sections') ? { Accept: 'application/json' } : {}), ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      if (!response.ok) throw new Error('Cart request failed: ' + response.status);
      const result = await response.json();
      if (result.status && Number(result.status) >= 400) throw new Error('Cart update rejected');
      return result;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function readCart() {
    const state = await request('cart.js');
    if (!Array.isArray(state.items)) throw new Error('Cart response is incomplete');
    cart = state;
    return state;
  }

  async function updateProtection(updates) {
    displayDirty = true;
    await request('cart/update.js', { updates, sections, sections_url: window.location.pathname });
    await readCart();
  }

  async function normalizeProtection() {
    const existing = protectedItems(cart);
    if (!existing.length) return;
    if (!shipmentItems(cart).length) {
      await updateProtection(Object.fromEntries(existing.map(item => [item.key, 0])));
    } else if (existing.length !== 1 || existing[0].quantity !== 1) {
      await updateProtection(Object.fromEntries(existing.map((item, index) => [item.key, index === 0 ? 1 : 0])));
    }
    if (!validProtection(cart)) throw new Error('Protection quantity could not be confirmed');
  }

  async function renderDrawer() {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', sections.join(','));
    let html;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      await waitForNativeCart();
      const revision = nativeRevision;
      html = await request(url.toString());
      if (revision === nativeRevision && !nativeBusy()) break;
      html = null;
      await waitForNativeCart();
      await readCart();
    }
    if (!html) throw new Error('Cart changed while its display was refreshing');
    const current = document.getElementById('CartDrawer');
    const parsed = new DOMParser().parseFromString(html['cart-drawer'] || '', 'text/html');
    const next = parsed.getElementById('CartDrawer');
    if (current) {
      const currentInner = current.querySelector('.drawer__inner');
      const nextInner = next?.querySelector('.drawer__inner');
      if (!currentInner || !nextInner) throw new Error('Cart display is incomplete');
      // Preserve the outer drawer and its overlay listeners. Replacing the inner
      // section reconnects native custom elements without the theme's gift renderer.
      currentInner.replaceWith(nextInner);
      const drawer = current.closest('cart-drawer');
      drawer?.classList.toggle('is-empty', cart.items.length === 0);
      drawerWasRendered = true;
      current.dispatchEvent(new CustomEvent('cart-drawer:update', {
        bubbles: true,
        detail: { source: 'loovi-package-protection', newTotalCount: String(cart.items.reduce((sum, item) => sum + item.quantity, 0)), checkFreeProduct: false }
      }));
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && html['cart-icon-bubble']) {
      const bubbleHTML = new DOMParser().parseFromString(html['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (bubbleHTML) bubble.innerHTML = bubbleHTML.innerHTML;
    }
    displayDirty = false;
    syncViews();

  }

  function scheduleAudit() {
    if (busy) { auditRequested = true; return; }
    clearTimeout(auditTimer);
    auditTimer = setTimeout(() => run('audit'), 80);
  }

  async function waitForNativeCart() {
    for (let attempt = 0; nativeBusy(); attempt += 1) {
      if (attempt >= 20) throw new Error('Native cart is still updating');
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  async function run(intent) {
    if (busy) {
      if (intent === 'audit') auditRequested = true;
      return;
    }
    clearTimeout(auditTimer);
    const previousFocus = document.activeElement;
    const hadDrawerFocus = Boolean(previousFocus?.closest('#CartDrawer'));
    drawerWasRendered = false;
    operationDeadline = Date.now() + 15000;
    busy = true;
    if (intent !== 'audit') status = 'Warenkorb wird aktualisiert …';
    syncViews();
    let canCheckout = false;
    let succeeded = false;
    try {
      await waitForNativeCart();
      await readCart();
      if (intent === true) {
        if (!shipmentItems(cart).length) throw new Error('Protection requires a shipment');
        if (!protectedItems(cart).length) {
          // Adding the paid service is only allowed after an explicit customer change.
          displayDirty = true;
          await request('cart/add.js', { items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname });
          await readCart();
        }
      } else if (intent === false && protectedItems(cart).length) {
        await updateProtection(Object.fromEntries(protectedItems(cart).map(item => [item.key, 0])));
      }
      // Audits and checkout also normalize duplicates and clear protection-only carts.
      await normalizeProtection();
      if (intent === true && (!shipmentItems(cart).length || protectedItems(cart).length !== 1)) throw new Error('Protection selection was not confirmed');
      if (intent === false && protectedItems(cart).length) throw new Error('Protection removal was not confirmed');
      if (displayDirty) await renderDrawer();
      canCheckout = intent === 'checkout' && shipmentItems(cart).length > 0 && !nativeBusy();
      status = intent === 'checkout' && !canCheckout ? 'Bitte lege zuerst ein Produkt in den Warenkorb.' : '';
      retryIntent = null;
      succeeded = true;
    } catch (error) {
      console.warn('[LOOVI Paketschutz] Cart update failed:', error.message);
      // A rejected response may still have changed the server cart. Re-read before showing state.
      try {
        await readCart();
        if (displayDirty) await renderDrawer();
      } catch (refreshError) {
        displayDirty = true;
      }
      const orphan = cart && !shipmentItems(cart).length && protectedItems(cart).length;
      status = orphan
        ? 'Der Paketschutz konnte noch nicht entfernt werden. Bitte erneut versuchen, bevor du zur Kasse gehst.'
        : 'Der Warenkorb konnte nicht zuverlässig aktualisiert werden. Bitte erneut versuchen.';
      retryIntent = orphan ? false : intent;
    } finally {
      busy = false;
      operationDeadline = 0;
      syncViews();
      const drawer = document.querySelector('cart-drawer.active');
      if (drawer && (hadDrawerFocus || drawerWasRendered) && typeof window.trapFocus === 'function') {
        const container = drawer.querySelector('#CartDrawer');
        let focus = previousFocus?.isConnected && !previousFocus.disabled ? previousFocus : null;
        if (!focus && previousFocus?.matches('[data-loovi-protection-toggle]')) focus = container.querySelector('[data-loovi-protection-toggle]:not(:disabled)');
        if (!focus) focus = container.querySelector('.drawer__close, .drawer__inner');
        window.trapFocus(container, focus || container);
      }
      // Only replay self-generated update events after success; failures need a visible retry.
      if (auditRequested && succeeded) { auditRequested = false; scheduleAudit(); }
      else auditRequested = false;
    }
    if (canCheckout) {
      const button = document.getElementById('CartDrawer-Checkout');
      if (button && !button.disabled) {
        allowCheckout = true;
        try { button.click(); } finally { allowCheckout = false; }
      }
    }
  }

  customElements.define('loovi-package-protection', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-loovi-protection-toggle]')) return;
        event.stopPropagation();
        run(event.target.checked);
      }, { signal: this.controller.signal });
      this.addEventListener('click', event => {
        if (!event.target.closest('[data-loovi-protection-retry]') || retryIntent === null) return;
        run(retryIntent);
      }, { signal: this.controller.signal });
      queueMicrotask(() => { if (this.isConnected) { syncViews(); scheduleAudit(); } });
    }
    disconnectedCallback() { this.controller?.abort(); }
  });

  ['cart-drawer:update', 'cart-drawer:render', 'cart-drawer:open'].forEach(name => document.addEventListener(name, () => {
    nativeRevision += 1;
    scheduleAudit();
  }));
  // Inert is the primary lock; capture listeners also protect older browser/event paths.
  ['click', 'change'].forEach(name => document.addEventListener(name, event => {
    if (!busy || !event.target.closest('#CartDrawer cart-drawer-items')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true));
  document.addEventListener('click', event => {
    if (!event.target.closest('#CartDrawer-Checkout') || allowCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    run('checkout');
  }, true);
  document.addEventListener('submit', event => {
    if (event.target.id !== 'CartDrawer-Form' || event.submitter?.name !== 'checkout' || allowCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    run('checkout');
  }, true);

  function start() {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, scheduleAudit);
    }
    scheduleAudit();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
